/**
 * Typed recipe API helpers — Track 2.D.1.
 *
 * Thin wrappers around the generic `ApiClient` (from `./index.ts`) that
 * narrow input/output types to recipe-specific shapes. Consumers import
 * from here rather than calling `api.recipe.*` directly, so the call sites
 * are insulated from oRPC procedure renames and input-schema changes.
 *
 * Architecture refs:
 *   functionality.md §3 — recipe domain model (slug, title, visibility)
 *   functionality.md §4.3 — promote + rollback (per-recipe defaultVariationUuid +
 *                            per-variation promotedAttemptUuid; two-tier go-to)
 *   architecture.md §4.7 — DDL: recipes, recipe_slug_history
 *   architecture.md §4.21 — quota scaffold (server-side only; no client action)
 *   architecture.md §4.23 — server-stamped author_agent; NOT accepted from client
 *
 * Security invariant (CLAUDE.md §3 + architecture.md §4.23):
 *   None of these helpers accept an `authorAgent` parameter. The server
 *   derives it from the session's client_kind. Any `authorAgent` field
 *   included in input would be logged and discarded by the oRPC procedure.
 *   We enforce this at the type level by omitting the field entirely.
 */

import type { ApiClient } from "./index.js";

// ── Input types ───────────────────────────────────────────────────────────────

export interface RecipeCreateInput {
  title: string;
  slug: string;
  description?: string | null;
  ratingSchemaJson?: string;
  visibility?: "private" | "public";
  forkUpstreamRecipeUuid?: string | null;
  forkUpstreamCommitUuid?: string | null;
}

export interface RecipeUpdateInput {
  uuid: string;
  title?: string;
  slug?: string;
  description?: string | null;
  /**
   * User-chosen display glyph (library page emoji picker). Full-unicode;
   * pass null to clear, omit to leave unchanged. Server bounds length ≤ 16.
   */
  emoji?: string | null;
  visibility?: "private" | "public";
  ratingSchemaJson?: string;
}

export interface RecipeListInput {
  limit?: number;
  cursor?: string | null;
  visibility?: "private" | "public" | null;
}

export interface RecipePromoteInput {
  recipeUuid: string;
  /** Pass null to clear the go-to pointer (unset canonical best). */
  commitUuid: string | null;
}

// ── Row type (mirrors the DO's RecipeRow) ─────────────────────────────────────

/**
 * Recipe row as returned by `recipe.get` / `recipe.list` / `recipe.create` etc.
 *
 * Two-tier go-to model (gap doc §2.6 A.6.1):
 *   - `defaultVariationUuid` — per-recipe pointer to the variation (branch)
 *     that is the recipe's canonical "go-to". Replaces the earlier single
 *     attempt-pointer that lived on the recipe row; the per-variation
 *     promoted attempt now lives on `BranchDto.promotedAttemptUuid`.
 *   - `null` = no go-to set (recipe has no canonical best yet).
 */
export interface RecipeRow {
  uuid: string;
  slug: string;
  title: string;
  description: string | null;
  /**
   * User-chosen display glyph for the recipe. NULL when the user has not set
   * one → the client renders the 🍳 fallback. The web maps `RecipeRow.emoji`
   * onto the client `Recipe.emoji`.
   */
  emoji: string | null;
  /**
   * UUID of the variation (branch) marked as this recipe's go-to. Two-tier
   * model: this points at a variation, not an attempt. The per-variation
   * promoted attempt is `BranchDto.promotedAttemptUuid`.
   */
  defaultVariationUuid: string | null;
  rating_schema_json: string;
  visibility: string;
  fork_upstream_recipe_uuid: string | null;
  fork_upstream_commit_uuid: string | null;
  /**
   * A-WP3 — feature-photo media UUID (the recipe's chosen face). NULL when unset
   * → the library card / Share hero falls back to emoji, then gradient. The web
   * maps this onto `Recipe.featureMediaUuid`.
   */
  feature_media_uuid?: string | null;
  /**
   * Group-share adoption provenance — present ONLY when this recipe was adopted
   * from a group share (family groups §3.3), carrying the sharer's handle + the
   * group name captured at import time. Absent for every other recipe (created,
   * within-library spin-off, seed-adopted). An absent field means "render
   * nothing"; the web maps this onto `RecipeHeader.spunOffFromLabel`. Both inner
   * fields are individually nullable (a sharer with no handle → `handle: null`).
   */
  spunOffFrom?: { handle: string | null; groupName: string | null };
  created_at: number;
  updated_at: number;
}

/**
 * Variation (branch) metadata as returned inline on each row of `recipe.list`.
 * Bug F1 fix (2026-05-03): the explorer tree needs variations on first paint
 * but body content is fetched lazily — only metadata travels with the list.
 *
 * `name` mirrors `slug` at MVP (Variation rename is wishlisted).
 * `isGoTo` is server-derived: true when the recipe's `defaultVariationUuid`
 * matches this variation's UUID (gap doc §2.6 A.6.1, two-tier go-to model).
 * `vN` is the count of attempts in cooked/promoted/archived status on this
 * variation — rendered as a small mono badge in the explorer (gap doc A.6.2).
 * Optional for backwards compat with older API responses that predate A.6.2.
 * `promotedAttemptUuid` is the per-variation promoted attempt pointer — the
 * second tier of the go-to model (gap doc §2.6 A.6.1). Replaces the earlier
 * recipe-level single-attempt-pointer for "which attempt is canonical".
 * `null` when no attempt has been promoted on this variation. Optional for
 * backwards compat while server-side rolls out.
 */
export interface RecipeListVariation {
  uuid: string;
  slug: string;
  name: string;
  isGoTo: boolean;
  /** Count of completed cooks (cooked | promoted | archived) on this variation. */
  vN?: number;
  /** Per-variation promoted attempt UUID (two-tier go-to). */
  promotedAttemptUuid?: string | null;
}

/** A row of `recipe.list` — RecipeRow + inlined variations metadata. */
export interface RecipeListRow extends RecipeRow {
  variations: RecipeListVariation[];
}

export interface RecipeListResult {
  recipe: RecipeRow;
}

export interface SlugHistoryEntry {
  recipe_uuid: string;
  slug: string;
  retired_at: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Create a new recipe.
 *
 * `author_agent` is server-stamped from the session; never accepted here.
 *
 * @throws `ORPCError("CONFLICT")` if `slug` is already taken for this user.
 * @throws `ORPCError("BAD_REQUEST")` if `slug` is malformed.
 */
export async function createRecipe(api: ApiClient, input: RecipeCreateInput): Promise<RecipeRow> {
  return (await api.recipe.create(input)) as RecipeRow;
}

/**
 * Fetch a single recipe by UUID.
 *
 * @throws `ORPCError("NOT_FOUND")` if no recipe with this UUID exists for
 *   the authenticated user.
 */
export async function getRecipe(api: ApiClient, uuid: string): Promise<RecipeRow> {
  return (await api.recipe.get({ uuid })) as RecipeRow;
}

/**
 * List recipes with keyset pagination.
 *
 * Returns up to `limit` (default 20, max 100) recipes ordered by UUID
 * (insertion order, because UUIDs are UUIDv7). Pass `nextCursor` from the
 * previous response as `cursor` to fetch the next page.
 *
 * Each row includes its `variations[]` (branches) inline as
 * `{ uuid, slug, name, isGoTo }` metadata — body content lives on attempts
 * and is loaded lazily by the editor, not by this list endpoint.
 */
export async function listRecipes(
  api: ApiClient,
  input: RecipeListInput = {},
): Promise<{ recipes: RecipeListRow[]; nextCursor: string | null }> {
  return (await api.recipe.list(input)) as {
    recipes: RecipeListRow[];
    nextCursor: string | null;
  };
}

/**
 * Update a recipe's mutable fields.
 *
 * Slug rename → `recipe_slug_history` row inserted automatically on the server.
 * Rating schema change → new `rating_schema_versions` row inserted; existing
 * attempts keep their old pointer.
 *
 * @throws `ORPCError("NOT_FOUND")` if the recipe UUID is unknown.
 * @throws `ORPCError("CONFLICT")` if the new slug is already taken.
 * @throws `ORPCError("BAD_REQUEST")` if the new slug is malformed.
 */
export async function updateRecipe(api: ApiClient, input: RecipeUpdateInput): Promise<RecipeRow> {
  return (await api.recipe.update(input)) as RecipeRow;
}

/**
 * Hard-delete a recipe and all its child rows (branches, attempts,
 * attempt_inputs, rating_schema_versions, slug_history, collection memberships).
 *
 * Soft delete is wishlisted per `.plan/implementation/2026-04-23-mvp-phased-plan.md`.
 *
 * @throws `ORPCError("NOT_FOUND")` if the recipe UUID is unknown.
 */
export async function deleteRecipe(api: ApiClient, uuid: string): Promise<{ ok: boolean }> {
  return (await api.recipe.delete({ uuid })) as { ok: boolean };
}

/**
 * Promote a committed attempt as the recipe's canonical go-to.
 *
 * Two-tier model (gap doc §2.6 A.6.1): server-side this updates the
 * variation's `promotedAttemptUuid` and points the recipe's
 * `defaultVariationUuid` at that variation in a single transaction.
 *
 * Rollback is the same call with an older attempt UUID (or `null` to clear).
 *
 * Spec ref: functionality.md §4.3 — "promote + rollback".
 *
 * @throws `ORPCError("NOT_FOUND")` if the recipe or attempt UUID is unknown,
 *   or if the attempt does not belong to a branch of this recipe.
 */
export async function promoteRecipe(
  api: ApiClient,
  input: RecipePromoteInput,
): Promise<{ ok: boolean }> {
  return (await api.recipe.promote(input)) as { ok: boolean };
}

/**
 * A-WP3 — set or clear the recipe's feature photo.
 *
 * `mediaUuid` is a photo on one of the recipe's own attempts; `null` clears the
 * feature photo. The server validates tree membership (a cross-recipe media UUID
 * → UNPROCESSABLE_CONTENT, surfaced as a viewer inline error — Rule 8).
 *
 * The ApiClient type is cast structurally (same pattern as the media client) so
 * this compiles without depending on the generated router type carrying the new
 * procedure — any real oRPC client forwards the call at runtime.
 */
export async function setRecipeFeatureMedia(
  api: ApiClient,
  input: { recipeUuid: string; mediaUuid: string | null },
): Promise<{ recipeUuid: string; featureMediaUuid: string | null }> {
  const client = api as unknown as {
    recipe: {
      setFeatureMedia(i: {
        recipeUuid: string;
        mediaUuid: string | null;
      }): Promise<{ recipeUuid: string; featureMediaUuid: string | null }>;
    };
  };
  return client.recipe.setFeatureMedia(input);
}

/**
 * List the slug-history rows for a recipe (slugs retired on rename).
 *
 * Useful for resolving old slugs in share links.
 *
 * @throws `ORPCError("NOT_FOUND")` if the recipe UUID is unknown.
 */
export async function listRecipeSlugHistory(
  api: ApiClient,
  recipeUuid: string,
): Promise<{ history: SlugHistoryEntry[] }> {
  return (await api.recipe.slugHistory({ uuid: recipeUuid })) as {
    history: SlugHistoryEntry[];
  };
}

/**
 * Spin off a recipe to a new sibling recipe in the user's library.
 *
 * The new recipe starts with the same title + branches as the source, but
 * no attempts (fresh canvas). Lineage is recorded server-side via
 * `fork_upstream_recipe_uuid` and `fork_upstream_commit_uuid` — these fields
 * are internal and not surfaced in the UI.
 *
 * Track 3.C spec ref: design.md §10.6, functionality.md §4.1.
 * Track 3.H: `sourcePublicUuid` is used for the from-Share path.
 *
 * @returns { uuid, slug } — the identity of the newly created recipe.
 * @throws `ORPCError("NOT_FOUND")` if the source recipe UUID is unknown.
 */
export async function spinOffRecipe(
  api: ApiClient,
  input: { sourceRecipeUuid: string } | { sourcePublicUuid: string },
): Promise<{ uuid: string; slug: string }> {
  return (await api.recipe.spinOff(input)) as {
    uuid: string;
    slug: string;
  };
}
